import { Component, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary - Captura erros de renderização em páginas lazy-loaded.
 * Usa exclusivamente variáveis CSS do design system.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary] Erro capturado:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="flex flex-col items-center justify-center"
          style={{
            minHeight: '60vh',
            fontFamily: 'var(--font-family-inter)',
            padding: '24px',
          }}
        >
          <div
            className="flex flex-col items-center gap-6 p-8 w-full"
            style={{
              maxWidth: '480px',
              backgroundColor: 'var(--card)',
              borderRadius: 'var(--radius-card)',
              border: '1px solid var(--border)',
              boxShadow: 'var(--elevation-sm)',
            }}
          >
            {/* Icon */}
            <div
              className="flex items-center justify-center"
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: 'var(--error-muted)',
                color: 'var(--error)',
              }}
            >
              <AlertTriangle size={32} />
            </div>

            {/* Message */}
            <div className="text-center">
              <h2
                style={{
                  fontWeight: 'var(--font-weight-semibold)',
                  fontSize: 'var(--text-lg)',
                  color: 'var(--foreground)',
                  marginBottom: '8px',
                }}
              >
                Algo deu errado
              </h2>
              <p
                style={{
                  color: 'var(--muted-foreground)',
                  fontSize: 'var(--text-sm)',
                  lineHeight: '1.6',
                  marginBottom: '12px',
                }}
              >
                Ocorreu um erro inesperado ao carregar esta pagina. 
                Tente recarregar ou voltar ao inicio.
              </p>

            </div>

            {/* Actions */}
            <div className="flex gap-3 w-full">
              <button
                onClick={this.handleGoHome}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 transition-opacity hover:opacity-80"
                style={{
                  backgroundColor: 'var(--muted)',
                  color: 'var(--foreground)',
                  borderRadius: 'var(--radius-button)',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family-inter)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                }}
              >
                <Home size={16} />
                Inicio
              </button>
              <button
                onClick={this.handleReload}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 transition-opacity hover:opacity-90"
                style={{
                  backgroundColor: 'var(--primary)',
                  color: 'var(--primary-foreground)',
                  borderRadius: 'var(--radius-button)',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family-inter)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                }}
              >
                <RefreshCw size={16} />
                Recarregar
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
